import { useCallback, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";

/**
 * usePreviewGate — small helper so any interactive component can check
 * whether the current user is in deferred/preview mode and, if so, dispatch
 * the global `jea:paywall` event (which AuthShell mounts a PaywallModal for)
 * instead of running the gated action.
 *
 *   const gate = usePreviewGate();
 *   <button onClick={gate.guardAction(() => addDebtor(), { feature: "adding debtors" })}>
 *     + Add Debtor
 *   </button>
 *   // or, spread:
 *   <button {...gate.guardedProps(() => addDebtor(), { feature: "adding debtors" })}>+</button>
 *
 * `paywall` + `openPaywall`/`closePaywall` remain for backwards compatibility
 * with components that own a local PaywallModal.
 */
export function usePreviewGate() {
  const { user } = useAuth();
  const inPreview = !!user && user.onboarding_deferred && !user.onboarding_completed;
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("this feature");

  const dispatchGlobal = useCallback((label) => {
    try {
      window.dispatchEvent(new CustomEvent("jea:paywall", {
        detail: { feature: label || "this feature" },
      }));
    } catch { /* ignore */ }
  }, []);

  const openPaywall = useCallback((label = "this feature") => {
    setFeature(label);
    setOpen(true);
    dispatchGlobal(label);
  }, [dispatchGlobal]);
  const closePaywall = useCallback(() => setOpen(false), []);

  /**
   * Wraps an action. Returns a NEW function. If preview → dispatch paywall.
   * Else calls the original `fn(...args)`.
   *
   * `opts.feature` — short label for the modal title ("adding debtors").
   * `opts.local` — if true, DON'T dispatch the global event, only open the
   *   local `paywall` state (for components with their own PaywallModal).
   */
  const guardAction = useCallback(
    (fn, opts = {}) => (...args) => {
      if (inPreview) {
        if (opts.local) {
          setFeature(opts.feature || "this feature");
          setOpen(true);
        } else {
          dispatchGlobal(opts.feature);
        }
        return;
      }
      return fn?.(...args);
    },
    [inPreview, dispatchGlobal]
  );

  /**
   * Spread-onto-button sugar. Same behavior as guardAction but returns
   * `{ onClick, 'aria-disabled' }` for direct spread.
   */
  const guardedProps = useCallback(
    (fn, opts = {}) => ({
      onClick: guardAction(fn, opts),
      "aria-disabled": inPreview ? true : undefined,
    }),
    [guardAction, inPreview]
  );

  /**
   * Legacy short-form (kept for backwards compat with earlier callers).
   *   const guard = usePreviewGate().guard;
   *   <button onClick={guard(fn, "adding debtors")}> ... </button>
   */
  const guard = useCallback(
    (fn, label = "this feature") => guardAction(fn, { feature: label }),
    [guardAction]
  );

  /**
   * Catches a 402 from the server and shows the paywall — useful for
   * defensive flows where a route may 402 despite frontend checks.
   */
  const handleServerError = useCallback((err, label = "this feature") => {
    if (err?.response?.status === 402) {
      openPaywall(label);
      return true;
    }
    toast.error(formatApiError(err));
    return false;
  }, [openPaywall]);

  return {
    inPreview,
    open,
    feature,
    openPaywall,
    closePaywall,
    guard,
    guardAction,
    guardedProps,
    handleServerError,
    paywall: { open, feature, onClose: closePaywall },
  };
}
